import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of, timer } from 'rxjs';
import {
  switchMap,
  map,
  mergeMap,
  catchError,
  takeWhile,
  take,
  tap,
  filter,
  delay,
} from 'rxjs/operators';

import Keycloak from 'keycloak-js';

import { ToastService } from '../../../common/toast-alert/toast.service';
import { MarketResearchService } from '../../services/market-research.service';
import {
  BATCH_STATUS_PROCESSING,
  BATCH_STATUS_COMPLETED,
  BATCH_STATUS_FAILED
} from '../../models/market-research.model';
import { MarketResearchState } from './market-research.state';
import * as MarketResearchActions from './market-research.actions';
import { resolveUserName } from '../current-user-name';

@Injectable()
export class MarketResearchEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly api = inject(MarketResearchService);
  private readonly toast = inject(ToastService);
  private readonly keycloak = inject(Keycloak);

  downloadTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.downloadTemplate),
      switchMap(() =>
        this.api.downloadTemplate().pipe(
          map((blob) => MarketResearchActions.downloadTemplateSuccess({ blob })),
          catchError((error) =>
            of(
              MarketResearchActions.downloadTemplateFail({
                error: error.message ?? 'Could not download template',
              })
            )
          )
        )
      )
    )
  );

  downloadTemplateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MarketResearchActions.downloadTemplateSuccess),
        tap(({ blob }) => this.saveBlob(blob, 'bulk_matching_template.xlsx'))
      ),
    { dispatch: false }
  );

  searchBrandOrgs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.searchBrandOrgs),
      switchMap(({ orgName }) =>
        this.api.searchBrands(orgName).pipe(
          map((brands) => MarketResearchActions.searchBrandOrgsSuccess({ brands })),
          catchError((error) =>
            of(
              MarketResearchActions.searchBrandOrgsFail({
                error: error.message ?? 'Could not search brands',
              })
            )
          )
        )
      )
    )
  );

  searchResumeBrandOrgs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.searchResumeBrandOrgs),
      switchMap(({ orgName }) =>
        this.api.searchBrands(orgName).pipe(
          map((brands) => MarketResearchActions.searchResumeBrandOrgsSuccess({ brands })),
          catchError((error) =>
            of(
              MarketResearchActions.searchResumeBrandOrgsFail({
                error: error.message ?? 'Could not search brands',
              })
            )
          )
        )
      )
    )
  );

  uploadFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.uploadFile),
      switchMap(({ file, params }) =>
        this.api.uploadExcel(file, params).pipe(
          map((response) => {
            const batchId = this.extractBatchId(response);
            if (!batchId) {
              throw new Error('No batch ID in response');
            }
            return MarketResearchActions.uploadFileSuccess({ batchId });
          }),
          catchError((error) =>
            of(
              MarketResearchActions.uploadFileFail({
                error: error.message ?? 'Upload failed',
              })
            )
          )
        )
      )
    )
  );

  uploadSuccessStartPolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.uploadFileSuccess),
      tap(() => this.toast.success('File uploaded — matching is running…')),
      map(({ batchId }) => MarketResearchActions.startPolling({ batchId }))
    )
  );

  startPolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.startPolling),
      switchMap(({ batchId }) =>
        of(MarketResearchActions.pollProgress({ batchId }))
      )
    )
  );

  pollProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.pollProgress),
      switchMap(({ batchId }) =>
        timer(0, 2000).pipe(
          switchMap(() => this.api.getProgress(batchId)),
          takeWhile((p) => p.batchStatus === BATCH_STATUS_PROCESSING, true),
          take(30),
          mergeMap((progress) => {
            const actions: Action[] = [MarketResearchActions.pollProgressTick({ progress })];
            if (progress.batchStatus !== BATCH_STATUS_PROCESSING) {
              actions.push(
                MarketResearchActions.pollProgressComplete({
                  batchId,
                  batchStatus: progress.batchStatus ?? BATCH_STATUS_FAILED
                })
              );
            }
            return actions;
          }),
          catchError((error) =>
            of(
              MarketResearchActions.pollProgressFail({
                error: error.message ?? 'Could not check progress',
              })
            )
          )
        )
      )
    )
  );

  // A FAILED batch has no review items and shouldn't force-navigate anywhere —
  // only a genuinely COMPLETED batch does.
  pollCompleteLoadReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.pollProgressComplete),
      filter(({ batchStatus }) => batchStatus === BATCH_STATUS_COMPLETED),
      map(({ batchId }) => MarketResearchActions.loadReviewItems({ batchId }))
    )
  );

  pollCompleteLoadAutoMatched$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.pollProgressComplete),
      filter(({ batchStatus }) => batchStatus === BATCH_STATUS_COMPLETED),
      map(({ batchId }) => MarketResearchActions.loadAutoMatchedItems({ batchId }))
    )
  );

  pollCompleteLoadNoMatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.pollProgressComplete),
      filter(({ batchStatus }) => batchStatus === BATCH_STATUS_COMPLETED),
      map(({ batchId }) => MarketResearchActions.loadNoMatchItems({ batchId }))
    )
  );

  pollCompleteLoadAllQueueItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.pollProgressComplete),
      filter(({ batchStatus }) => batchStatus === BATCH_STATUS_COMPLETED),
      map(({ batchId }) => MarketResearchActions.loadAllQueueItems({ batchId }))
    )
  );

  /**
   * Matching finishing didn't used to move the wizard forward — this is the
   * fix. The delay is deliberate: a small batch can complete in under a
   * second, and without it the Matching screen would mount and unmount in
   * the same tick — the real counts would never get a chance to render.
   */
  pollCompleteAdvanceStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.pollProgressComplete),
      filter(({ batchStatus }) => batchStatus === BATCH_STATUS_COMPLETED),
      delay(1200),
      map(() => MarketResearchActions.goToStep({ step: 3 }))
    )
  );

  checkResumeProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.checkResumeProgress),
      switchMap(({ batchId, alwaysGoToResolve }) =>
        this.api.getProgress(batchId).pipe(
          mergeMap((progress) => {
            const ready = !alwaysGoToResolve && (progress.readyForDownload ?? false);
            return [
              MarketResearchActions.pollProgressTick({ progress }),
              MarketResearchActions.goToStep({ step: ready ? 4 : 3 }),
            ];
          }),
          catchError((error) =>
            of(
              MarketResearchActions.pollProgressFail({
                error: error.message ?? 'Could not load batch progress',
              }),
              MarketResearchActions.goToStep({ step: 3 })
            )
          )
        )
      )
    )
  );

  loadReviewItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.loadReviewItems),
      switchMap(({ batchId }) =>
        this.api.getReviewItems(batchId).pipe(
          map((response) =>
            MarketResearchActions.loadReviewItemsSuccess({ response })
          ),
          catchError((error) =>
            of(
              MarketResearchActions.loadReviewItemsFail({
                error: error.message ?? 'Could not load review items',
              })
            )
          )
        )
      )
    )
  );

  loadAutoMatched$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.loadAutoMatchedItems),
      switchMap(({ batchId }) =>
        this.api.getAutoMatchedItems(batchId).pipe(
          map((response) =>
            MarketResearchActions.loadAutoMatchedItemsSuccess({
              items: response.items ?? [],
            })
          ),
          catchError((error) =>
            of(
              MarketResearchActions.loadAutoMatchedItemsFail({
                error: error.message ?? 'Could not load auto-matched items',
              })
            )
          )
        )
      )
    )
  );

  loadNoMatchItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.loadNoMatchItems),
      switchMap(({ batchId }) =>
        this.api.getItemsByStatus(batchId, 'NO_MATCH,AUTO_REJECTED').pipe(
          map((response) =>
            MarketResearchActions.loadNoMatchItemsSuccess({
              items: response.items ?? [],
            })
          ),
          catchError((error) =>
            of(
              MarketResearchActions.loadNoMatchItemsFail({
                error: error.message ?? 'Could not load no-match items',
              })
            )
          )
        )
      )
    )
  );

  loadAllQueueItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.loadAllQueueItems),
      switchMap(({ batchId }) =>
        this.api.getItemsByStatus(batchId).pipe(
          map((response) =>
            MarketResearchActions.loadAllQueueItemsSuccess({
              items: response.items ?? [],
            })
          ),
          catchError((error) =>
            of(
              MarketResearchActions.loadAllQueueItemsFail({
                error: error.message ?? 'Could not load all rows',
              })
            )
          )
        )
      )
    )
  );

  searchBatches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.searchBatches),
      switchMap(({ brandId, fromDate, toDate }) =>
        this.api.searchBatches(brandId, fromDate, toDate).pipe(
          map((response) =>
            MarketResearchActions.searchBatchesSuccess({
              batches: response.batches ?? [],
            })
          ),
          catchError((error) =>
            of(
              MarketResearchActions.searchBatchesFail({
                error: error.message ?? 'Could not load batches',
              })
            )
          )
        )
      )
    )
  );

  selectBatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.selectBatch),
      map(({ batch }) =>
        MarketResearchActions.loadReviewItems({ batchId: batch.batchId ?? '' })
      )
    )
  );

  selectBatchAutoMatched$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.selectBatch),
      map(({ batch }) =>
        MarketResearchActions.loadAutoMatchedItems({ batchId: batch.batchId ?? '' })
      )
    )
  );

  selectBatchNoMatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.selectBatch),
      map(({ batch }) =>
        MarketResearchActions.loadNoMatchItems({ batchId: batch.batchId ?? '' })
      )
    )
  );

  selectBatchAllQueueItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.selectBatch),
      map(({ batch }) =>
        MarketResearchActions.loadAllQueueItems({ batchId: batch.batchId ?? '' })
      )
    )
  );

  confirmMatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.confirmMatch),
      switchMap(({ queueId, bhiveId, manualMatch }) =>
        this.api.selectSuggestion(queueId, bhiveId).pipe(
          map(() => MarketResearchActions.confirmMatchSuccess({ queueId, bhiveId, manualMatch })),
          catchError((error) =>
            of(
              MarketResearchActions.confirmMatchFail({
                error: error.message ?? 'Could not confirm match',
              })
            )
          )
        )
      )
    )
  );

  confirmMatchSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MarketResearchActions.confirmMatchSuccess),
        tap(() => (this.toast as any).success('Match confirmed'))
      ),
    { dispatch: false }
  );

  confirmAutoMatchedAbove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.confirmAutoMatchedAbove),
      switchMap(({ batchId, minScore }) =>
        this.api.confirmAutoMatchedAbove(batchId, minScore).pipe(
          map((result) =>
            MarketResearchActions.confirmAutoMatchedAboveSuccess({
              batchId,
              confirmed: result.confirmed ?? 0,
              failedNames: (result.failures ?? []).map(
                (f) => f.companyName || `row ${f.rowIndex ?? f.queueId}`
              ),
            })
          ),
          catchError((error) =>
            of(
              MarketResearchActions.confirmAutoMatchedAboveFail({
                error: error.message ?? 'Could not bulk confirm matches',
              })
            )
          )
        )
      )
    )
  );

  /**
   * A bulk confirm moves an unbounded number of rows at once, so patching them
   * individually in the reducer would be guesswork — refetch the three lists and the
   * live counts instead.
   */
  confirmAutoMatchedAboveSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.confirmAutoMatchedAboveSuccess),
      tap(({ confirmed, failedNames }) => {
        (this.toast as any).success(
          `${confirmed} match${confirmed === 1 ? '' : 'es'} confirmed`
        );
        // A row the backend failed to confirm stays in the list — say which, not nothing.
        if (failedNames.length > 0) {
          (this.toast as any).warn(
            `${failedNames.length} could not be confirmed: ${failedNames.join(', ')}. Confirm ${failedNames.length === 1 ? 'it' : 'them'} one by one.`
          );
        }
      }),
      mergeMap(({ batchId }) => [
        MarketResearchActions.loadReviewItems({ batchId }),
        MarketResearchActions.loadAutoMatchedItems({ batchId }),
        MarketResearchActions.loadNoMatchItems({ batchId }),
        MarketResearchActions.loadAllQueueItems({ batchId }),
      ])
    )
  );

  rejectMatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.rejectMatch),
      switchMap(({ queueId }) =>
        this.api.rejectMatch(queueId).pipe(
          map(() => MarketResearchActions.rejectMatchSuccess({ queueId })),
          catchError((error) =>
            of(
              MarketResearchActions.rejectMatchFail({
                error: error.message ?? 'Could not reject match',
              })
            )
          )
        )
      )
    )
  );

  rejectMatchSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MarketResearchActions.rejectMatchSuccess),
        tap(() => (this.toast as any).success('Match rejected'))
      ),
    { dispatch: false }
  );

  saveToDatabase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.saveToDatabase),
      switchMap(({ batchId, mode }) =>
        this.api.importConnections(batchId, mode, resolveUserName(this.keycloak)).pipe(
          map((result) => MarketResearchActions.saveToDatabaseSuccess({ result })),
          catchError((error) =>
            of(
              MarketResearchActions.saveToDatabaseFail({
                error: error.message ?? 'Save failed',
              })
            )
          )
        )
      )
    )
  );

  saveSucess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MarketResearchActions.saveToDatabaseSuccess),
        tap(({ result }) => {
          const skipped = result?.skippedRows ?? 0;
          if (skipped > 0) {
            (this.toast as any).warn(
              `Saved to database, but ${skipped} row${skipped === 1 ? ' was' : 's were'} skipped ` +
                `and not imported`
            );
          } else {
            (this.toast as any).success('Saved to database');
          }

          // The registry write succeeded regardless — the graph is a mirror that the
          // backend's retry cron replays — so this is a warning, not an error. Without it
          // a total Neo4j outage looked like a clean success.
          const failed = result?.neo4jFailed ?? 0;
          if (failed > 0) {
            const detail = result?.neo4jError ? ` (${result.neo4jError})` : '';
            (this.toast as any).warn(
              `Graph sync incomplete — ${failed} relationship${failed === 1 ? '' : 's'} ` +
                `could not be written to Neo4j and are queued for retry${detail}`
            );
          }
        })
      ),
    { dispatch: false }
  );

  downloadResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MarketResearchActions.downloadResults),
      switchMap(({ batchId }) =>
        this.api.downloadBatchResults(batchId).pipe(
          map((blob) => MarketResearchActions.downloadResultsSuccess({ blob, batchId })),
          catchError((error) =>
            of(
              MarketResearchActions.downloadResultsFail({
                error: error.message ?? 'Could not download results',
              })
            )
          )
        )
      )
    )
  );

  downloadResultsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MarketResearchActions.downloadResultsSuccess),
        tap(({ blob, batchId }) => this.saveBlob(blob, `matching-results-${batchId}.xlsx`))
      ),
    { dispatch: false }
  );

  handleErrors$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          MarketResearchActions.searchBrandOrgsFail,
          MarketResearchActions.searchResumeBrandOrgsFail,
          MarketResearchActions.uploadFileFail,
          MarketResearchActions.pollProgressFail,
          MarketResearchActions.loadReviewItemsFail,
          MarketResearchActions.loadAutoMatchedItemsFail,
          MarketResearchActions.loadNoMatchItemsFail,
          MarketResearchActions.loadAllQueueItemsFail,
          MarketResearchActions.searchBatchesFail,
          MarketResearchActions.confirmMatchFail,
          MarketResearchActions.confirmAutoMatchedAboveFail,
          MarketResearchActions.rejectMatchFail,
          MarketResearchActions.saveToDatabaseFail,
          MarketResearchActions.downloadResultsFail
        ),
        tap(({ error }) => (this.toast as any).error(error))
      ),
    { dispatch: false }
  );


  private extractBatchId(response: Record<string, any>): string | null {
    const candidate =
      response['batchId'] ??
      response['batch_id'] ??
      response['id'] ??
      response['batchID'];
    return candidate != null ? String(candidate) : null;
  }

  private saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }
}
